// Phase 9 /admin/engineering — surface /api/health/selfcheck + /api/backend/describe.
//
// This replaces the standalone /backend page as the canonical admin
// "what does the box look like right now?" surface.
import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { backend, health } from "@/api/endpoints";
import { formatApiError } from "@/lib/formatApiError";

function Section({ title, children, testId }) {
    return (
        <section
            className="rounded-lg border border-ux-border-1 bg-ux-bg-1 p-4"
            data-testid={testId}
        >
            <h2 className="text-[12px] uppercase tracking-[0.18em] text-ux-text-3 mb-2">
                {title}
            </h2>
            <div className="text-[13px] text-ux-text-2">{children}</div>
        </section>
    );
}

function KV({ items }) {
    return (
        <dl className="grid grid-cols-[180px_1fr] gap-x-3 gap-y-1 text-[12px]">
            {items.map(([k, v]) => (
                <span key={k} className="contents">
                    <dt className="text-ux-text-3">{k}</dt>
                    <dd className="text-ux-text-2 font-mono truncate">{v}</dd>
                </span>
            ))}
        </dl>
    );
}

export default function AdminEngineeringPage() {
    const [selfcheck, setSelfcheck] = useState(null);
    const [describe, setDescribe] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);

    const refresh = async () => {
        setLoading(true);
        try {
            const [sc, dc] = await Promise.all([
                health.selfcheck(),
                backend.describe(),
            ]);
            setSelfcheck(sc);
            setDescribe(dc);
            setErr("");
        } catch (e) {
            setErr(formatApiError(e, "Failed to load"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    const jsbsim = selfcheck?.jsbsim || {};
    const mongo = selfcheck?.mongo || {};
    const presets = selfcheck?.presets_root || {};
    const queue = selfcheck?.queue || {};

    return (
        <div data-testid="admin-engineering-page">
            <PageHeader
                title="Engineering Settings"
                subtitle="Backend self-check, runtime flags, and queue depth."
                actions={[
                    <button
                        key="r"
                        onClick={refresh}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] border border-ux-border-1 hover:bg-ux-bg-2"
                        data-testid="admin-engineering-refresh"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Refresh
                    </button>,
                ]}
            />

            {err && (
                <div className="mb-4 p-3 rounded-md border border-ux-danger/40 bg-ux-danger/10 text-ux-danger text-[13px]">
                    {err}
                </div>
            )}

            {loading ? (
                <div className="text-ux-text-3">loading…</div>
            ) : (
                <div className="grid gap-3 md:grid-cols-2">
                    <Section title="JSBSim" testId="admin-engineering-jsbsim">
                        <KV
                            items={[
                                ["installed", String(jsbsim.installed ?? false)],
                                ["version", jsbsim.version ?? "—"],
                                ["root", jsbsim.root ?? "—"],
                            ]}
                        />
                    </Section>
                    <Section title="MongoDB" testId="admin-engineering-mongo">
                        <KV
                            items={[
                                ["connected", String(mongo.connected ?? false)],
                                ["database", mongo.database ?? "—"],
                                ["uptime_s", mongo.uptime_s ?? "—"],
                            ]}
                        />
                    </Section>
                    <Section title="Presets root" testId="admin-engineering-presets">
                        <KV
                            items={[
                                ["exists", String(presets.exists ?? false)],
                                ["path", presets.path ?? "—"],
                                ["families", presets.families ?? "—"],
                            ]}
                        />
                    </Section>
                    <Section title="Queue" testId="admin-engineering-queue">
                        <KV
                            items={[
                                ["running_runs", String(selfcheck?.running_runs ?? 0)],
                                ["queued_runs", String(selfcheck?.queued_runs ?? 0)],
                                ["max_concurrent", String(selfcheck?.max_concurrent ?? "—")],
                                ["queue_poll_ms", String(queue.poll_ms ?? "—")],
                            ]}
                        />
                    </Section>
                    <Section title="Backend describe" testId="admin-engineering-describe">
                        <pre className="text-[11px] text-ux-text-2 font-mono whitespace-pre-wrap overflow-auto max-h-[280px]">
                            {JSON.stringify(describe, null, 2)}
                        </pre>
                    </Section>
                    <Section title="Raw selfcheck" testId="admin-engineering-raw">
                        <pre className="text-[11px] text-ux-text-2 font-mono whitespace-pre-wrap overflow-auto max-h-[280px]">
                            {JSON.stringify(selfcheck, null, 2)}
                        </pre>
                    </Section>
                </div>
            )}
        </div>
    );
}
